/**
 * @file forgot-password.component.ts
 * @description
 * This component handles the "Forgot Password" functionality for the Superadmin Panel.
 * It allows users to request a password reset by providing their registered email.
 *
 * The component includes the following:
 * 1. A form with an input field for:
 *    - Email (Required, Valid Email Format)
 * 2. Form validation to ensure the email field is properly filled.
 * 3. API integration to send a password reset request to the backend.
 * 4. Displays a success message if the reset email is sent successfully.
 * 5. Error handling and user feedback (invalid email, network issues, etc.).
 * 6. Navigation option to return to the login page.
 *
 * @dependencies:
 * - Authentication Service for handling forgot password requests.
 * - Angular Reactive Forms (`@angular/forms`) for form handling and validation.
 * - Angular Router (`@angular/router`) for navigation.
 * - Angular Material Snackbar (`MatSnackBar`) for displaying success and error messages.
 *
 * @param {MatSnackBar} snackBar - Used to display notifications for success and error messages.
 */

import { Component } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { encryptData, showNotification } from '../../../consts/utils';
import { superAdminHttpRoutes, superAdminHttpVersion } from '../../../consts/super_admin_http_routes';
import { ApiService } from '../../../shared/services/api.services';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SeoService } from '../../../shared/services/seo.service';

@Component({
  selector: 'app-forgot-password',
  imports: [ReactiveFormsModule],
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.scss']
})

export class ForgotPasswordModalComponent {

  form: UntypedFormGroup;

  constructor(private modal: NgbModal, private apiService: ApiService, private seo: SeoService,
    private _formBuilder: UntypedFormBuilder, private snackBar: MatSnackBar) {
    /**
         * Sets up SEO meta tags using SeoService
         * This helps in optimizing the page for search engines and social media previews.
     */
    this.seo.setTags({
      title: 'SuperAdmin Forgot Password Form', // Title
      titleSuffix: '- Shining On Safety', // Title Suffix
      description: 'Shining On Safety', // Description
      image: '', // Image
      keywords: 'Shining On Safety' // Keywords
    });
  }

  ngOnInit(): void {
    //Called after the constructor, initializing input properties, and the first call to ngOnChanges.
    //Add 'implements OnInit' to the class.
    this.form = this._formBuilder.group({
      email: ['',
        [
          Validators.compose([
            Validators.required,
            Validators.email,
            Validators.maxLength(200),
            Validators.minLength(4)
          ])
        ],
      ]
    });
  }
  /**
   * Dismiss the model
   */
  close() {
    this.modal.dismissAll();
  }

  /**
   * Forgot Password API call 
   */

  forgotPassword() {
    const username = this.form.get('email')?.value;
    if (!username || username.length < 1) {
      showNotification(this.snackBar, 'Invalid email', 'error');
    }
    if (username) {
      if (this.form.invalid) {
        this.form.markAllAsTouched();
        return;
      }
      const encryptedEmail = encryptData(this.form.value.email);

      const formData = {
        email: encryptedEmail,
      };

      this.apiService.httpPostCallWithoutToken<any>(
        superAdminHttpVersion.V1 + superAdminHttpRoutes.FORGOT_PASSWORD,
        formData
      ).subscribe({
        next: (response: any) => {
          if (response.status) {
            this.modal.dismissAll();
            showNotification(this.snackBar, response.message, 'success');
          } else {
            showNotification(this.snackBar, response.message, 'error');
          }
        }, error: (error: any) => {
          showNotification(this.snackBar, error.message, 'error');
        }
      });
    }
  }
}
